import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { useYupValidationResolver } from 'hooks/useYupValidationResolver/useYupValidationResolver';
import {
  DocumentUnderstandingAgentSettings,
  useDocumentUnderstandingAgentRelationships,
  useDocumentUnderstandingAgentSettings,
} from 'services/repositories/documentUnderstandingAgent/documentUnderstandingAgent';
import { TabsList } from 'components/Navigation/Tabs/TabsList';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';

import { SettingsSidebar } from '../SettingsSidebar/SettingsSidebar';
import { ByConnectionsTab } from './components/ByConnectionsTab/ByConnectionsTab';
import { GlobalSettingsTab } from './components/GlobalSettingsTab/GlobalSettingsTab';
import {
  DEFAULT_DOCUMENT_UNDERSTANDING_AGENT_FORM_VALUES,
  EditableDocumentUnderstandingAgentSections,
  hasConnectionOverrides,
  isValidEmail,
  isValidSenderPattern,
  PRICE_STRATEGY_LABEL_KEYS,
} from './helpers';

const TAB_PARAM = 'tab';
const GLOBAL_TAB = 'global';
const BY_CONNECTIONS_TAB = 'connections';

export const DocumentUnderstandingAgent = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: settings } = useDocumentUnderstandingAgentSettings();
  const { data: relationships, isError: isRelationshipsError } = useDocumentUnderstandingAgentRelationships();
  // The overrides tab is read-only/transparency-only, so hide it when there is nothing to show. Keep it
  // visible on error so the failure surfaces instead of the tab silently disappearing.
  const showOverridesTab = isRelationshipsError || (relationships ?? []).some(hasConnectionOverrides);

  const formSchema = Yup.object({
    extraction: Yup.object({
      prices_strategy: Yup.string().oneOf(Object.keys(PRICE_STRATEGY_LABEL_KEYS)).required(),
    }),
    sender_filter: Yup.object({
      whitelist: Yup.array()
        .of(Yup.string().required())
        .test(
          'valid-sender-patterns',
          t('settings:documentUnderstandingAgent.global.senderFilter.invalidPattern') ?? '',
          (values) => (values ?? []).every(isValidSenderPattern),
        )
        .required(),
      blacklist: Yup.array()
        .of(Yup.string().required())
        .test(
          'valid-sender-patterns',
          t('settings:documentUnderstandingAgent.global.senderFilter.invalidPattern') ?? '',
          (values) => (values ?? []).every(isValidSenderPattern),
        )
        .required(),
    }),
    replies_filter: Yup.object({
      emails: Yup.array()
        .of(Yup.string().required())
        .test(
          'valid-emails',
          t('settings:documentUnderstandingAgent.global.replyRecipients.invalidEmail') ?? '',
          (values) => (values ?? []).every(isValidEmail),
        )
        .required(),
      notifications_enabled: Yup.boolean().required(),
    }),
  });

  const resolver = useYupValidationResolver(formSchema);
  const methods = useForm<EditableDocumentUnderstandingAgentSections>({
    resolver,
    values: getFormValues(settings),
  });

  const requestedTab = searchParams.get(TAB_PARAM) === BY_CONNECTIONS_TAB ? BY_CONNECTIONS_TAB : GLOBAL_TAB;
  const selectedTab = requestedTab === BY_CONNECTIONS_TAB && showOverridesTab ? BY_CONNECTIONS_TAB : GLOBAL_TAB;
  const selectedIndex = selectedTab === BY_CONNECTIONS_TAB ? 1 : 0;

  const tabs = [
    { label: t('settings:documentUnderstandingAgent.tabs.global') },
    ...(showOverridesTab ? [{ label: t('settings:documentUnderstandingAgent.tabs.byConnections') }] : []),
  ];

  const onTabChange = (index: number) => {
    setSearchParams({ [TAB_PARAM]: index === 1 ? BY_CONNECTIONS_TAB : GLOBAL_TAB });
  };

  return (
    <FormProvider {...methods}>
      <Page>
        <Page.Head title={t('common:mainMenu.settings')} />

        <Page.Section leftAside={<SettingsSidebar />}>
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-bold text-black">{t('settings:documentUnderstandingAgent.title')}</h3>
            <p className="text-sm text-gray-500">{t('settings:documentUnderstandingAgent.description')}</p>
          </div>

          <div className="mt-6">
            <TabsList tabs={tabs} selected={selectedIndex} onChange={onTabChange} />
          </div>

          <div className="mt-6">
            {selectedTab === GLOBAL_TAB ? (
              <div data-testid="document-understanding-agent-global-panel">
                <GlobalSettingsTab />
              </div>
            ) : (
              <div data-testid="document-understanding-agent-by-connections-panel">
                <ByConnectionsTab />
              </div>
            )}
          </div>
        </Page.Section>
      </Page>

      <UnsavedChangesModal shouldBlock={methods.formState.isDirty} ignoreSearchParamChanges />
    </FormProvider>
  );
};

const getFormValues = (
  settings: DocumentUnderstandingAgentSettings | undefined,
): EditableDocumentUnderstandingAgentSections => {
  const { extraction, replies_filter: repliesFilter, sender_filter: senderFilter } = settings ?? {};

  return {
    extraction: {
      prices_strategy:
        extraction?.prices_strategy ?? DEFAULT_DOCUMENT_UNDERSTANDING_AGENT_FORM_VALUES.extraction.prices_strategy,
    },
    sender_filter: {
      whitelist: senderFilter?.whitelist ?? [],
      blacklist: senderFilter?.blacklist ?? [],
    },
    replies_filter: {
      emails: repliesFilter?.emails ?? [],
      notifications_enabled: !(repliesFilter?.suppress_on_success ?? false),
    },
  };
};
